import { txToast } from "@/components/TxToaster";
import { cn, copyToClipboard } from "@/lib/utils";
import { CopyIcon, ListTodoIcon, Trash2Icon, UndoIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOpenPromptDialog } from "@/hooks/dialogs";
import { shortenId, sortPlayerIds } from "@shared/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Button } from "./ui/button";
import { HoverTip } from '@/components/ui/tooltip';


//MARK: Helpers
//To help ctrl+c'ing with the list selected
const InvisibleNewLine = () => <span className="opacity-0">{'\n'}</span>;

//Used in the compare prompt dialog
const placeholderIds = [
    'fivem:xxxxxxx',
    'license:xxxxxxxxxxxxxx',
    'discord:xxxxxxxxxxxxxxxxxx',
    'etc...',
].join('\n');
const placeholderHwids = [
    '2:xxxxxxxxxxxxxx...',
    '4:xxxxxxxxxxxxxx...',
    '5:xxxxxxxxxxxxxx...',
    'etc...',
].join('\n');


//MARK: IdLine
type IdLineProps = {
    id: string;
    displayShortenedId: boolean;
    isOnline: boolean;
    highlightOnline: boolean;
    onCopy?: () => void;
    isComparing: boolean;
    isMatch: boolean;
    isRemoving: boolean;
    isMarkedForRemoval?: boolean;
    toggleMarkRemoval?: () => void;
    disableButtons: boolean;
}

function IdLine({
    id,
    displayShortenedId,
    onCopy,
    isOnline,
    highlightOnline,
    isComparing,
    isMatch,
    isRemoving,
    isMarkedForRemoval,
    toggleMarkRemoval,
    disableButtons,
}: IdLineProps) {
    const canBeRemoved = !id.startsWith('license:') && !isOnline; // Only allow removal of old IDs
    const isBold = (isComparing && isMatch)
        || (!isComparing && !highlightOnline)
        || (!isComparing && highlightOnline && isOnline);
    return (
        <div className="relative flex justify-between items-center gap-2 group/line">
            <HoverTip label={displayShortenedId ? id : undefined}>
                <span
                    className={cn(
                        'block px-1',
                        isBold ? 'font-semibold opacity-100' : 'opacity-50',
                        isComparing && isMatch && 'text-success-inline',
                        isMarkedForRemoval && 'line-through text-destructive-inline',
                    )}
                    aria-label={displayShortenedId ? id : undefined}
                >
                    {displayShortenedId ? shortenId(id, 20) : id}<InvisibleNewLine />
                </span>
            </HoverTip>
            {isRemoving ? canBeRemoved && (
                <HoverTip label={isMarkedForRemoval ? 'Undo mark for deletion' : 'Mark for deletion'}>
                    <button
                        className="absolute right-0 top-0 h-full flex items-center bg-background hover:bg-background group/removal"
                        onClick={toggleMarkRemoval}
                        aria-label={isMarkedForRemoval ? 'Undo mark for deletion' : 'Mark for deletion'}
                        disabled={disableButtons}
                    >
                        {isMarkedForRemoval ? (
                            <UndoIcon className="h-4 opacity-75 group-hover:opacity-100 group-hover/removal:text-warning" />
                        ) : (
                            <XIcon className="h-4 opacity-75 group-hover:opacity-100 group-hover/removal:text-destructive" />
                        )}
                    </button>
                </HoverTip>
            ) : (
                <HoverTip label='Copy to clipboard'>
                    <button
                        className="absolute right-0 top-0 h-full flex items-center opacity-0 group-hover/line:opacity-100 bg-background hover:bg-background group/copy transition-opacity"
                        onClick={onCopy}
                        aria-label='Copy to clipboard'
                        disabled={disableButtons}
                    >
                        <CopyIcon className="h-4 opacity-75 group-hover/copy:opacity-100 group-hover/copy:text-primary" />
                    </button>
                </HoverTip>
            )}
        </div>
    );
}


//MARK: IdListControls
type IdListControlsProps = {
    hasRemoveIds: boolean;
    hasAnyId: boolean;
    canCopy: boolean;
    copyLabel: string;
    emptyReason: React.ReactNode;
    copyBlockedReason: React.ReactNode;
    removalBlockedReason?: React.ReactNode;
    handleStartMarkRemoval: () => void;
    handleCompareIds: () => void;
    handleCopyAllIds: () => void;
    typeStr: string;
}

function RemoveIdsButton({ blockedReason, handleStartMarkRemoval, typeStr }: {
    blockedReason?: React.ReactNode;
    handleStartMarkRemoval: () => void;
    typeStr: string;
}) {
    const isBlocked = !!blockedReason;
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                {/* a disabled button emits no pointer events, so the hover lives on the wrapper */}
                <span
                    tabIndex={isBlocked ? 0 : -1}
                    className={cn('inline-flex', isBlocked && 'cursor-not-allowed')}
                >
                    <button
                        onClick={handleStartMarkRemoval}
                        disabled={isBlocked}
                        className={cn(isBlocked && 'pointer-events-none')}
                    >
                        <Trash2Icon className={cn(
                            'h-5 opacity-50',
                            isBlocked ? 'opacity-35' : 'hover:opacity-100 hover:text-primary'
                        )} />
                    </button>
                </span>
            </TooltipTrigger>
            <TooltipContent className={cn(isBlocked && 'text-destructive-inline text-center')}>
                {isBlocked ? blockedReason : <p>{`Mark ${typeStr}s for deletion.`}</p>}
            </TooltipContent>
        </Tooltip>
    );
}

function IdControlButton({ label, blocked, blockedReason, onClick, children }: {
    label: string;
    blocked?: boolean;
    blockedReason?: React.ReactNode;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                {/* a disabled button emits no pointer events, so the hover lives on the wrapper */}
                <span
                    tabIndex={blocked ? 0 : -1}
                    className={cn('inline-flex', blocked && 'cursor-not-allowed')}
                >
                    <button
                        onClick={onClick}
                        disabled={blocked}
                        aria-label={label}
                        className={cn(blocked && 'pointer-events-none')}
                    >
                        {children}
                    </button>
                </span>
            </TooltipTrigger>
            <TooltipContent className={cn(blocked && 'text-destructive-inline text-center')}>
                {blocked && blockedReason ? blockedReason : <p>{label}</p>}
            </TooltipContent>
        </Tooltip>
    );
}

function IdListControls({
    hasRemoveIds,
    hasAnyId,
    canCopy,
    copyLabel,
    emptyReason,
    copyBlockedReason,
    removalBlockedReason,
    handleStartMarkRemoval,
    handleCompareIds,
    handleCopyAllIds,
    typeStr,
}: IdListControlsProps) {
    return (
        <>
            {hasRemoveIds && (
                <RemoveIdsButton
                    blockedReason={removalBlockedReason}
                    handleStartMarkRemoval={handleStartMarkRemoval}
                    typeStr={typeStr}
                />
            )}
            <IdControlButton
                label={`Compare ${typeStr}s`}
                blocked={!hasAnyId}
                blockedReason={emptyReason}
                onClick={handleCompareIds}
            >
                <ListTodoIcon className={cn('h-6', hasAnyId
                    ? 'opacity-50 hover:opacity-100 hover:text-primary'
                    : 'opacity-35')} />
            </IdControlButton>
            <IdControlButton
                label={copyLabel}
                blocked={!canCopy}
                blockedReason={copyBlockedReason}
                onClick={handleCopyAllIds}
            >
                <CopyIcon className={cn('h-5', canCopy
                    ? 'opacity-50 hover:opacity-100 hover:text-primary'
                    : 'opacity-35')} />
            </IdControlButton>
        </>
    )
}


//MARK: Main Component
//the previous ids get their own header, so the feedback has to know where to land
type FeedbackSlot = 'main' | 'previous';
type ActionFeedback = {
    msg: string;
    type: 'green' | 'yellow' | 'red';
    slot: FeedbackSlot;
}

type MultiIdsListProps = {
    idsOnline?: string[];
    idsOffline: string[];
    type: 'hwid' | 'id';
    src: 'player' | 'action';
    isHwids?: boolean;
    onRemoveIds?: (
        ids: string[],
        onError: () => void,
    ) => void;
    canRemoveIds?: boolean;
}

export default function MultiIdsList({ idsOnline, idsOffline, type, src, onRemoveIds, canRemoveIds }: MultiIdsListProps) {
    const openPromptDialog = useOpenPromptDialog();
    const divRef = useRef<HTMLDivElement>(null);
    const msgRef = useRef<any>(null);
    const [compareMatches, setCompareMatches] = useState<string[] | null>(null);
    const [markedForRemoval, setMarkedForRemoval] = useState<string[] | null>(null);
    const [actionFeedback, setActionFeedback] = useState<ActionFeedback | false>(false);
    const [isCommittingDeletions, setIsCommittingDeletions] = useState(false);

    const handleClearState = () => {
        setCompareMatches(null);
        setMarkedForRemoval(null);
        setActionFeedback(false);
        setIsCommittingDeletions(false);
    }

    const hasAnyIdAvailable = !!(idsOnline?.length || idsOffline.length);
    const hasAnyIdOnline = Array.isArray(idsOnline) && !!idsOnline.length;
    const currentIds = useMemo(() => {
        const ids: string[] = [];
        idsOnline?.slice().sort(sortPlayerIds).forEach((id) => {
            if (!ids.includes(id)) ids.push(id);
        });
        return ids;
    }, [idsOnline]);
    const previousIds = useMemo(() => {
        const ids: string[] = [];
        idsOffline.slice().sort(sortPlayerIds).forEach((id) => {
            if (!ids.includes(id) && !currentIds.includes(id)) ids.push(id);
        });
        return ids;
    }, [idsOffline, currentIds]);
    const allIds = useMemo(() => [...currentIds, ...previousIds], [currentIds, previousIds]);
    //the backend refuses ids in use and the license, so those are never deletable
    const removableIds = useMemo(
        () => previousIds.filter((id) => !id.startsWith('license:')),
        [previousIds],
    );

    const isHwids = type === 'hwid';
    const typeStr = isHwids ? 'HWID' : 'ID';
    const getPluralizedType = (count: number) => count === 1 ? typeStr : `${typeStr}s`;
    const emptyMessage = `This ${src} has no ${typeStr}s.`;
    const noCurrentMessage = 'No identifiers in use right now.';
    const noPreviousMessage = 'No previous identifiers saved.';
    const isInCompareMode = Array.isArray(compareMatches);
    const isCompareIdMatch = (id: string) => isInCompareMode && compareMatches.includes(id);
    const isInRemovalMode = Array.isArray(markedForRemoval);
    const splitPrevious = !isHwids && Array.isArray(idsOnline);
    const removalSlot: FeedbackSlot = splitPrevious ? 'previous' : 'main';
    //the header only copies what is listed under it
    const copyableIds = splitPrevious ? currentIds : allIds;
    const listClasses = 'font-mono break-all whitespace-pre-wrap rounded-[inherit] overflow-hidden divide-y divide-border/50 text-muted-foreground text-xs leading-6 tracking-wider';
    const isMarkedForRemoval = (id: string) => isInRemovalMode && markedForRemoval.includes(id);
    const removalBlockedReason = !canRemoveIds ? (
        <p>
            You do not have the permission <br />
            required to remove {typeStr}s
        </p>
    ) : !removableIds.length ? (
        <p>
            There are no {typeStr}s that can be deleted. <br />
            Only {typeStr}s not in use right now can be removed.
        </p>
    ) : undefined;

    useEffect(() => {
        msgRef.current?.classList.remove('animate-toastbar-leave');
        msgRef.current?.classList.add('animate-toastbar-enter');
        if (actionFeedback) {
            const timer1 = setTimeout(() => {
                msgRef.current?.classList.remove('animate-toastbar-enter');
                msgRef.current?.classList.add('animate-toastbar-leave');
            }, 2500);
            const timer2 = setTimeout(() => {
                setActionFeedback(false);
            }, 2750);

            return () => {
                clearTimeout(timer1);
                clearTimeout(timer2);
            };
        } else {

        }
    }, [actionFeedback, isInCompareMode, isInRemovalMode]);


    const handleStartMarkRemoval = () => {
        if (!hasAnyIdAvailable) {
            txToast.warning(`There are no ${typeStr}s to remove.`);
            return;
        }
        if (!idsOffline.length) {
            txToast.warning(`You can only remove ${typeStr}s not being used by this player right now, and all the ${typeStr}s in the list are currently being used.`);
            return;
        }
        setMarkedForRemoval([]);
    }

    const toggleMarkRemoval = (id: string) => {
        console.log(`toggleMarkRemoval(${id})`, markedForRemoval);
        if (!isInRemovalMode) return;
        setMarkedForRemoval((prev) => {
            if (!prev) return [id];
            if (prev.includes(id)) {
                return prev.filter((i) => i !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleCommitRemoval = () => {
        if (!onRemoveIds || !isInRemovalMode || !markedForRemoval?.length) return;
        setActionFeedback({
            msg: 'saving...',
            type: 'yellow',
            slot: removalSlot,
        });
        setIsCommittingDeletions(true);
        onRemoveIds(markedForRemoval, () => {
            //We don't need to handle success cases because the modal refreshes
            setIsCommittingDeletions(false);
            setActionFeedback({
                msg: 'Error:(',
                type: 'red',
                slot: removalSlot,
            });
        });
    }

    const handleCompareIds = () => {
        openPromptDialog({
            title: `Compare ${typeStr}s`,
            message: <p>
                Paste in a list of {typeStr}s to compare with the current list. <br />
                Separate each {typeStr} with a new line or comma.
            </p>,
            placeholder: isHwids ? placeholderHwids : placeholderIds,
            submitLabel: 'Compare',
            required: true,
            isMultiline: true,
            isWide: true,
            onSubmit: (input) => {
                const cleanIds = input
                    .split(/[\n\s,;]+/)
                    .map((id) => id.trim())
                    .filter((id) => id.length && allIds.includes(id));
                setCompareMatches(cleanIds);
            }
        });
    };

    const handleCopyString = (strToCopy: string, slot: FeedbackSlot = 'main') => {
        if (!divRef.current) throw new Error(`divRef.current undefined`);
        copyToClipboard(strToCopy, divRef.current).then((res) => {
            if (res !== false) {
                setActionFeedback({
                    msg: 'Copied',
                    type: 'green',
                    slot,
                });
            } else {
                txToast.error('Failed to copy to clipboard :(');
            }
        }).catch((error) => {
            txToast.error({
                title: 'Failed to copy to clipboard:',
                msg: error.message,
            });
            setActionFeedback({
                msg: 'Error :(',
                type: 'red',
                slot,
            });
        });
    }

    const handleCopyAllIds = () => {
        if (!divRef.current) throw new Error(`divRef.current undefined`);
        if (!copyableIds.length) return;
        handleCopyString(copyableIds.join('\r\n'));
    }

    const handleCopyPreviousIds = () => {
        if (!divRef.current) throw new Error(`divRef.current undefined`);
        if (!previousIds.length) return;
        handleCopyString(previousIds.join('\r\n'), 'previous');
    }
    const handleCopySingleId = (id: string) => {
        if (!divRef.current) throw new Error(`divRef.current undefined`);
        if (!id) return;
        handleCopyString(id);
    }

    return <div>
        <div className="flex justify-between items-center pb-1" ref={divRef}>
            <h3 className="text-xl">
                {isHwids
                    ? 'All Hardware IDs'
                    : splitPrevious ? 'Current Identifiers' : 'Player Identifiers'}
                {isInCompareMode && compareMatches.length ? (
                    <span className="ml-2 text-sm font-normal italic text-success-inline">
                        ({compareMatches.length} {compareMatches.length === 1 ? 'match' : 'matches'} found)
                    </span>
                ) : null}
            </h3>
            <div className="w-24 min-h-6 flex justify-end gap-2.5">
                {actionFeedback && actionFeedback.slot === 'main' ? (
                    <span
                        ref={msgRef}
                        className={cn(
"w-full text-right text-sm select-none pointer-events-none",
                            actionFeedback.type === 'green' && "text-success-inline",
                            actionFeedback.type === 'yellow' && "text-warning-inline",
                            actionFeedback.type === 'red' && "text-destructive-inline",
                        )}
                    >
                        {actionFeedback.msg}
                    </span>
                ) : isInCompareMode ? (
                    <Button ref={msgRef} variant="outline" size="inline" onClick={handleClearState}
                        className="hover:border-danger/70 hover:bg-danger/20 hover:text-danger-soft">
                        Clear <XIcon className="size-4" />
                    </Button>
                ) : isInRemovalMode ? (
                    //while splitting, the removal controls live on the previous list
                    splitPrevious ? null : (
                        <Button ref={msgRef} variant="outline" size="inline" onClick={handleClearState} className="group hover:border-danger/70 hover:bg-danger/20 hover:text-danger-soft">
                            Cancel <XIcon className="size-4" />
                        </Button>
                    )
                ) : (
                    <IdListControls
                        hasRemoveIds={!!onRemoveIds && !splitPrevious}
                        hasAnyId={hasAnyIdAvailable}
                        canCopy={!!copyableIds.length}
                        copyLabel={`Copy all ${splitPrevious ? 'current ' : ''}${typeStr}s`}
                        emptyReason={<p>{emptyMessage}</p>}
                        copyBlockedReason={<p>{hasAnyIdAvailable ? noCurrentMessage : emptyMessage}</p>}
                        removalBlockedReason={removalBlockedReason}
                        handleStartMarkRemoval={handleStartMarkRemoval}
                        handleCompareIds={handleCompareIds}
                        handleCopyAllIds={handleCopyAllIds}
                        typeStr={typeStr}
                    />
                )}
            </div>
        </div>
        <div className="relative">
            <div className="border rounded">
                <p className={listClasses}>
                    {!hasAnyIdAvailable && <span className="block px-1 opacity-50 italic">{emptyMessage}</span>}
                    {hasAnyIdAvailable && splitPrevious && !currentIds.length && (
                        <span className="block px-1 opacity-50 italic">{noCurrentMessage}</span>
                    )}
                    {(splitPrevious ? currentIds : allIds).map((id) => (
                        <IdLine
                            key={id}
                            id={id}
                            isOnline={idsOnline?.includes(id) ?? false}
                            displayShortenedId={isHwids}
                            onCopy={() => handleCopySingleId(id)}
                            highlightOnline={hasAnyIdOnline}
                            isComparing={isInCompareMode}
                            isMatch={isCompareIdMatch(id)}
                            isRemoving={isInRemovalMode}
                            isMarkedForRemoval={isMarkedForRemoval(id)}
                            toggleMarkRemoval={() => toggleMarkRemoval(id)}
                            disableButtons={isCommittingDeletions}
                        />
                    ))}
                </p>
            </div>
            {hasAnyIdAvailable && splitPrevious && (
                <>
                    <div className="flex items-center justify-between gap-2 pt-3 pb-1">
                        <h3 className="text-xl">Previously Used Identifiers</h3>
                        {/* only these can be deleted, the current ones are in use */}
                        <div className="min-h-6 flex items-center justify-end gap-2.5">
                            {actionFeedback && actionFeedback.slot === 'previous' ? (
                                <span
                                    ref={msgRef}
                                    className={cn(
"text-right text-sm select-none pointer-events-none",
                                        actionFeedback.type === 'green' && "text-success-inline",
                                        actionFeedback.type === 'yellow' && "text-warning-inline",
                                        actionFeedback.type === 'red' && "text-destructive-inline",
                                    )}
                                >
                                    {actionFeedback.msg}
                                </span>
                            ) : isInRemovalMode ? (
                                onRemoveIds ? (
                                    <Button variant="outline" size="inline" onClick={handleClearState} className="group hover:border-danger/70 hover:bg-danger/20 hover:text-danger-soft">
                                        Cancel <XIcon className="size-4" />
                                    </Button>
                                ) : null
                            ) : (
                                <>
                                    {onRemoveIds ? (
                                        <RemoveIdsButton
                                            blockedReason={removalBlockedReason}
                                            handleStartMarkRemoval={handleStartMarkRemoval}
                                            typeStr={typeStr}
                                        />
                                    ) : null}
                                    <IdControlButton
                                        label={`Copy all previous ${typeStr}s`}
                                        blocked={!previousIds.length}
                                        blockedReason={<p>{noPreviousMessage}</p>}
                                        onClick={handleCopyPreviousIds}
                                    >
                                        <CopyIcon className={cn('h-5', previousIds.length
                                            ? 'opacity-50 hover:opacity-100 hover:text-primary'
                                            : 'opacity-35')} />
                                    </IdControlButton>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="border rounded">
                        <p className={listClasses}>
                            {!previousIds.length && (
                                <span className="block px-1 opacity-50 italic">{noPreviousMessage}</span>
                            )}
                            {previousIds.map((id) => (
                                <IdLine
                                    key={id}
                                    id={id}
                                    isOnline={false}
                            displayShortenedId={isHwids}
                            onCopy={() => handleCopySingleId(id)}
                            highlightOnline={hasAnyIdOnline}
                            isComparing={isInCompareMode}
                            isMatch={isCompareIdMatch(id)}
                            isRemoving={isInRemovalMode}
                            isMarkedForRemoval={isMarkedForRemoval(id)}
                            toggleMarkRemoval={() => toggleMarkRemoval(id)}
                            disableButtons={isCommittingDeletions}
                                />
                            ))}
                        </p>
                    </div>
                </>
            )}
            {isInRemovalMode && (
                <div className="mt-1 rounded-bar px-2 py-1 flex flex-wrap justify-center xs:justify-between items-center gap-2 border border-danger/45 bg-destructive-hint text-fg font-sans text-xs leading-6 tracking-wider select-none">
                    <span className="block font-semibold my-[1px]">
                        {markedForRemoval.length
                            ? `Permanently delete ${markedForRemoval.length} ${getPluralizedType(markedForRemoval.length)}?`
                            : 'Click above to mark IDs for deletion.'}
                    </span>
                    <Button
                        size="inline"
                        variant="destructive"
                        className={cn(
                            markedForRemoval.length ? 'inline-flex' : 'hidden',
                            isCommittingDeletions && 'cursor-progress'
                        )}
                        onClick={handleCommitRemoval}
                        title={`Confirm removal of marked ${typeStr}s.`}
                        disabled={isCommittingDeletions}
                    >
                        {isCommittingDeletions ? 'Saving...' : 'Confirm & Delete'}
                    </Button>
                </div>
            )}
            {isInCompareMode && !compareMatches.length && (
                <>
                    <div className="absolute inset-0 bg-black/25 rounded-[inherit] backdrop-blur-sm flex items-center justify-center p-4">
                        <span className="text-xl tracking-wider text-warning-inline">No matching {typeStr} found.</span>
                    </div>
                </>
            )}
        </div>
    </div>
}
